import { ArrowRight, Check } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MultiStepForm from "@/components/MultiStepForm";
import { useLang, Hreflang } from "@/i18n/LanguageContext";
import { catPath, catByKey, ALL_CATS } from "@/i18n/slugs";
import { FAMILY_TYPES, CATEGORY_PRICES, familyByKey } from "@/i18n/taxonomy";
import { categoryDataNL } from "@/i18n/content-nl";
import { TestimonialsCarousel } from "@/components/TestimonialsCarousel";

const categoryData = {
  toiture: {
    title: "Devis Toiture",
    description: "Recevez jusqu'à 5 devis gratuits pour vos travaux de toiture",
    seoTitle: "Devis Toiture Belgique | Rénovation & Réparation | DevisMaison.be",
    image: "https://images.pexels.com/photos/13075346/pexels-photo-13075346.jpeg?auto=compress&cs=tinysrgb&w=1200",
    content: {
      intro: "La toiture est l'élément le plus important de votre maison. Une toiture en bon état garantit une protection optimale contre les intempéries et une bonne isolation thermique.",
      types: [
        "Rénovation complète de toiture",
        "Remplacement de tuiles ou ardoises",
        "Isolation de toiture",
        "Réparation de fuites",
        "Installation de Velux",
        "Zinguerie et gouttières"
      ],
      prices: [
        { type: "Rénovation toiture complète", price: "100-180 €/m²" },
        { type: "Remplacement tuiles", price: "40-80 €/m²" },
        { type: "Isolation toiture", price: "30-60 €/m²" },
        { type: "Réparation fuites", price: "150-400 €" }
      ]
    }
  },
  renovation: {
    title: "Devis Rénovation",
    description: "Recevez jusqu'à 5 devis gratuits pour votre projet de rénovation",
    seoTitle: "Devis Rénovation Maison Belgique | Travaux Complets | DevisMaison.be",
    image: "https://images.unsplash.com/photo-1772442198620-3674b427e59e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    content: {
      intro: "La rénovation de votre maison augmente sa valeur et améliore votre confort quotidien. Que ce soit pour une rénovation complète ou partielle, nos artisans qualifiés sont là pour vous.",
      types: [
        "Rénovation complète de maison",
        "Rénovation d'appartement",
        "Extension de maison",
        "Aménagement de combles",
        "Rénovation énergétique",
        "Mise aux normes électriques"
      ],
      prices: [
        { type: "Rénovation légère", price: "300-600 €/m²" },
        { type: "Rénovation moyenne", price: "600-1000 €/m²" },
        { type: "Rénovation lourde", price: "1000-1500 €/m²" },
        { type: "Extension", price: "1500-2500 €/m²" }
      ]
    }
  },
  chassis: {
    title: "Devis Châssis & Fenêtres",
    description: "Recevez jusqu'à 5 devis gratuits pour vos châssis et fenêtres",
    seoTitle: "Devis Châssis & Fenêtres Belgique | Double Vitrage | DevisMaison.be",
    image: "https://images.unsplash.com/photo-1616843221271-a3447ca74df3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    content: {
      intro: "Le remplacement de vos châssis améliore l'isolation thermique et acoustique de votre habitation. Choisissez parmi le PVC, l'aluminium ou le bois selon vos besoins.",
      types: [
        "Châssis PVC",
        "Châssis aluminium",
        "Châssis bois",
        "Double vitrage",
        "Triple vitrage",
        "Portes d'entrée"
      ],
      prices: [
        { type: "Fenêtre PVC standard", price: "400-700 €" },
        { type: "Fenêtre aluminium", price: "600-1000 €" },
        { type: "Porte-fenêtre", price: "800-1500 €" },
        { type: "Porte d'entrée", price: "1500-3000 €" }
      ]
    }
  },
  isolation: {
    title: "Devis Isolation",
    description: "Recevez jusqu'à 5 devis gratuits pour l'isolation de votre maison",
    seoTitle: "Devis Isolation Belgique | Murs, Toiture, Sols | DevisMaison.be",
    image: "https://images.unsplash.com/photo-1752852381090-d3e0edf8f373?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    content: {
      intro: "Une bonne isolation réduit vos factures d'énergie et améliore le confort de votre habitation. Profitez également des primes énergie disponibles en Belgique.",
      types: [
        "Isolation toiture",
        "Isolation murs extérieurs",
        "Isolation murs intérieurs",
        "Isolation sols",
        "Isolation cave",
        "Isolation acoustique"
      ],
      prices: [
        { type: "Isolation toiture par l'intérieur", price: "30-50 €/m²" },
        { type: "Isolation murs creux", price: "20-35 €/m²" },
        { type: "Isolation façade (crépi)", price: "100-180 €/m²" },
        { type: "Isolation sol", price: "25-45 €/m²" }
      ]
    }
  },
  "salle-de-bain": {
    title: "Devis Salle de Bain",
    description: "Recevez jusqu'à 5 devis gratuits pour la rénovation de votre salle de bain",
    seoTitle: "Devis Salle de Bain Belgique | Rénovation Complète | DevisMaison.be",
    image: "https://images.pexels.com/photos/32998547/pexels-photo-32998547.jpeg?w=1200",
    content: {
      intro: "La salle de bain est une pièce essentielle de votre maison. Une rénovation bien pensée augmente le confort et la valeur de votre bien immobilier.",
      types: [
        "Rénovation complète salle de bain",
        "Remplacement baignoire/douche",
        "Installation douche italienne",
        "Carrelage murs et sols",
        "Plomberie sanitaire",
        "Meuble vasque et rangements"
      ],
      prices: [
        { type: "Rénovation salle de bain (4m²)", price: "5000-10000 €" },
        { type: "Douche italienne", price: "1500-3500 €" },
        { type: "Carrelage (pose)", price: "40-80 €/m²" },
        { type: "Meuble vasque", price: "500-2000 €" }
      ]
    }
  },
  terrasse: {
    title: "Devis Terrasse",
    description: "Recevez jusqu'à 5 devis gratuits pour votre terrasse",
    seoTitle: "Devis Terrasse Belgique | Bois, Béton, Carrelage | DevisMaison.be",
    image: "https://images.unsplash.com/photo-1768527341242-62055b63260b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    content: {
      intro: "Une terrasse bien aménagée prolonge votre espace de vie et augmente la valeur de votre propriété. Découvrez les différentes options disponibles.",
      types: [
        "Terrasse en bois",
        "Terrasse en composite",
        "Terrasse en béton",
        "Terrasse carrelée",
        "Terrasse en pierre naturelle",
        "Pergola et aménagements"
      ],
      prices: [
        { type: "Terrasse bois (avec pose)", price: "80-150 €/m²" },
        { type: "Terrasse composite", price: "100-180 €/m²" },
        { type: "Dalle béton", price: "50-100 €/m²" },
        { type: "Carrelage extérieur", price: "70-120 €/m²" }
      ]
    }
  },
  piscine: {
    title: "Devis Piscine",
    description: "Recevez jusqu'à 5 devis gratuits pour votre piscine",
    seoTitle: "Devis Piscine Belgique | Construction & Rénovation | DevisMaison.be",
    image: "https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?w=1200&q=80",
    content: {
      intro: "Une piscine privée apporte confort et plaisir tout en valorisant votre propriété. Comparez les offres pour trouver la solution adaptée à votre budget.",
      types: [
        "Piscine enterrée béton",
        "Piscine coque polyester",
        "Piscine hors-sol",
        "Rénovation piscine existante",
        "Équipements et accessoires",
        "Abri de piscine"
      ],
      prices: [
        { type: "Piscine coque (8x4m)", price: "20000-35000 €" },
        { type: "Piscine béton (8x4m)", price: "30000-50000 €" },
        { type: "Piscine hors-sol", price: "3000-15000 €" },
        { type: "Abri piscine", price: "5000-20000 €" }
      ]
    }
  },
  chauffage: {
    title: "Devis Chauffage",
    description: "Recevez jusqu'à 5 devis gratuits pour votre système de chauffage",
    seoTitle: "Devis Chauffage Belgique | Pompe à Chaleur, Chaudière | DevisMaison.be",
    image: "https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=1200&q=80",
    content: {
      intro: "Un système de chauffage performant réduit vos factures d'énergie et améliore votre confort. Profitez des primes pour le remplacement de votre ancienne chaudière.",
      types: [
        "Pompe à chaleur air/eau",
        "Pompe à chaleur air/air",
        "Chaudière à condensation gaz",
        "Chaudière à pellets",
        "Chauffage au sol",
        "Radiateurs"
      ],
      prices: [
        { type: "Pompe à chaleur", price: "8000-15000 €" },
        { type: "Chaudière condensation gaz", price: "3000-6000 €" },
        { type: "Chaudière pellets", price: "10000-18000 €" },
        { type: "Chauffage au sol (rénovation)", price: "50-80 €/m²" }
      ]
    }
  },
  electricite: {
    title: "Devis Électricité",
    description: "Recevez jusqu'à 5 devis gratuits pour vos travaux électriques",
    seoTitle: "Devis Électricité Belgique | Installation & Rénovation | DevisMaison.be",
    image: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=1200&q=80",
    content: {
      intro: "Une installation électrique aux normes est essentielle pour la sécurité de votre habitation. Faites appel à des électriciens certifiés pour tous vos travaux.",
      types: [
        "Mise en conformité électrique",
        "Rénovation installation complète",
        "Tableau électrique",
        "Installation prises et interrupteurs",
        "Éclairage intérieur/extérieur",
        "Domotique"
      ],
      prices: [
        { type: "Mise en conformité", price: "1500-4000 €" },
        { type: "Rénovation complète (maison)", price: "6000-15000 €" },
        { type: "Tableau électrique", price: "800-2000 €" },
        { type: "Point lumineux (ajout)", price: "100-200 €" }
      ]
    }
  },
  plafonnage: {
    title: "Devis Plafonnage",
    description: "Recevez jusqu'à 5 devis gratuits pour vos travaux de plafonnage",
    seoTitle: "Devis Plafonnage Belgique | Plâtre & Enduit | DevisMaison.be",
    image: "https://images.unsplash.com/photo-1768839725085-829e6ac7ac26?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    content: {
      intro: "Le plafonnage est essentiel pour obtenir des murs lisses et prêts à peindre. Nos plafonneurs professionnels garantissent une finition impeccable pour tous vos projets.",
      types: [
        "Plafonnage murs neufs",
        "Rénovation plafonnage existant",
        "Enduit décoratif",
        "Crépi intérieur",
        "Lissage de surfaces",
        "Plafonds suspendus"
      ],
      prices: [
        { type: "Plafonnage standard", price: "20-35 €/m²" },
        { type: "Enduit décoratif", price: "40-70 €/m²" },
        { type: "Réparation fissures", price: "15-30 €/m²" },
        { type: "Plafond suspendu", price: "45-80 €/m²" }
      ]
    }
  },
  menuiserie: {
    title: "Devis Menuiserie Intérieur",
    description: "Recevez jusqu'à 5 devis gratuits pour vos travaux de menuiserie intérieure",
    seoTitle: "Devis Menuiserie Intérieur Belgique | Portes & Escaliers | DevisMaison.be",
    image: "https://images.unsplash.com/photo-1537233907675-e1207a765f91?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    content: {
      intro: "La menuiserie intérieure apporte caractère et fonctionnalité à votre habitat. Portes, escaliers, placards sur mesure - nos menuisiers créent des solutions adaptées à vos besoins.",
      types: [
        "Portes intérieures",
        "Escaliers sur mesure",
        "Placards et dressings",
        "Parquet et plancher",
        "Plinthes et moulures",
        "Bibliothèques intégrées"
      ],
      prices: [
        { type: "Porte intérieure (pose)", price: "250-600 €" },
        { type: "Escalier sur mesure", price: "3000-10000 €" },
        { type: "Placard sur mesure", price: "800-3000 €" },
        { type: "Parquet (pose)", price: "35-70 €/m²" }
      ]
    }
  },
  "amenagement-exterieur": {
    title: "Devis Aménagement Extérieur",
    description: "Recevez jusqu'à 5 devis gratuits pour l'aménagement de vos extérieurs",
    seoTitle: "Devis Aménagement Extérieur Belgique | Jardin & Allées | DevisMaison.be",
    image: "https://images.unsplash.com/photo-1761637823730-fab1f300084d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    content: {
      intro: "L'aménagement extérieur valorise votre propriété et crée des espaces de vie agréables. Jardins, allées, clôtures - transformez vos extérieurs avec des professionnels qualifiés.",
      types: [
        "Création de jardin",
        "Allées et pavage",
        "Clôtures et portails",
        "Éclairage extérieur",
        "Murs de soutènement",
        "Abris de jardin"
      ],
      prices: [
        { type: "Allée pavée", price: "60-120 €/m²" },
        { type: "Clôture standard", price: "80-150 €/mètre" },
        { type: "Portail automatique", price: "2000-5000 €" },
        { type: "Abri de jardin", price: "1500-8000 €" }
      ]
    }
  },
  "entreprise-generale": {
    title: "Devis Entreprise Générale",
    description: "Recevez jusqu'à 5 devis gratuits pour vos travaux de construction",
    seoTitle: "Devis Entreprise Générale Belgique | Construction & Gros Œuvre | DevisMaison.be",
    image: "https://images.unsplash.com/photo-1672627170267-fca17bb54156?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    content: {
      intro: "Une entreprise générale coordonne l'ensemble de vos travaux de construction ou rénovation. Un seul interlocuteur pour tous vos corps de métier, simplifiant la gestion de votre projet.",
      types: [
        "Construction neuve",
        "Rénovation complète",
        "Gros œuvre",
        "Extension de maison",
        "Transformation de bâtiment",
        "Coordination de chantier"
      ],
      prices: [
        { type: "Construction neuve", price: "1500-2500 €/m²" },
        { type: "Rénovation complète", price: "800-1500 €/m²" },
        { type: "Extension", price: "1800-3000 €/m²" },
        { type: "Gros œuvre seul", price: "600-1000 €/m²" }
      ]
    }
  },
  carrelage: {
    title: "Devis Carrelage",
    description: "Recevez jusqu'à 5 devis gratuits pour vos travaux de carrelage",
    seoTitle: "Devis Carrelage Belgique | Sol & Mur | DevisMaison.be",
    image: "https://images.unsplash.com/photo-1560005627-c96e0aeb6eaa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    content: {
      intro: "Le carrelage apporte durabilité et esthétique à vos sols et murs. Nos carreleurs professionnels réalisent des poses soignées pour tous types de carreaux.",
      types: [
        "Carrelage sol intérieur",
        "Carrelage mural",
        "Faïence salle de bain",
        "Carrelage terrasse",
        "Mosaïque décorative",
        "Rénovation carrelage"
      ],
      prices: [
        { type: "Carrelage sol (pose)", price: "35-60 €/m²" },
        { type: "Carrelage mural", price: "40-70 €/m²" },
        { type: "Carrelage grand format", price: "50-90 €/m²" },
        { type: "Mosaïque", price: "80-150 €/m²" }
      ]
    }
  },
  cuisine: {
    title: "Devis Cuisine",
    description: "Recevez jusqu'à 5 devis gratuits pour votre projet de cuisine",
    seoTitle: "Devis Cuisine Belgique | Aménagement & Rénovation | DevisMaison.be",
    image: "https://images.unsplash.com/photo-1772567732969-c1506edf80a0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    content: {
      intro: "La cuisine est le cœur de votre maison. Que vous souhaitiez une rénovation complète ou un simple rafraîchissement, nos cuisinistes professionnels créent l'espace de vos rêves.",
      types: [
        "Cuisine complète sur mesure",
        "Rénovation de cuisine existante",
        "Remplacement plan de travail",
        "Installation électroménagers",
        "Îlot central",
        "Aménagement petite cuisine"
      ],
      prices: [
        { type: "Cuisine entrée de gamme", price: "3 000-6 000 €" },
        { type: "Cuisine milieu de gamme", price: "6 000-15 000 €" },
        { type: "Cuisine haut de gamme", price: "15 000-30 000 €" },
        { type: "Cuisine sur mesure", price: "20 000-50 000 €" }
      ]
    }
  },
  "extension-maison": {
    title: "Devis Extension Maison",
    description: "Recevez jusqu'à 5 devis gratuits pour l'extension de votre maison",
    seoTitle: "Devis Extension Maison Belgique | Agrandissement | DevisMaison.be",
    image: "https://images.pexels.com/photos/4692281/pexels-photo-4692281.jpeg?auto=compress&cs=tinysrgb&w=1200",
    content: {
      intro: "Agrandissez votre espace de vie sans déménager. Une extension de maison augmente la surface habitable et la valeur de votre bien immobilier.",
      types: [
        "Extension latérale",
        "Surélévation de toiture",
        "Véranda habitable",
        "Annexe indépendante",
        "Aménagement de combles",
        "Extension en ossature bois"
      ],
      prices: [
        { type: "Extension classique", price: "1 500-2 500 €/m²" },
        { type: "Surélévation", price: "1 800-3 000 €/m²" },
        { type: "Véranda", price: "1 000-2 000 €/m²" },
        { type: "Ossature bois", price: "1 200-2 200 €/m²" }
      ]
    }
  },
  "pompe-a-chaleur": {
    title: "Devis Pompe à Chaleur",
    description: "Recevez jusqu'à 5 devis gratuits pour l'installation d'une pompe à chaleur",
    seoTitle: "Devis Pompe à Chaleur Belgique | Installation PAC | DevisMaison.be",
    image: "https://images.pexels.com/photos/20046693/pexels-photo-20046693.jpeg?auto=compress&cs=tinysrgb&w=1200",
    content: {
      intro: "La pompe à chaleur est la solution de chauffage écologique et économique par excellence. Réduisez vos factures d'énergie et votre empreinte carbone.",
      types: [
        "Pompe à chaleur air-eau",
        "Pompe à chaleur air-air",
        "Pompe à chaleur géothermique",
        "Chauffe-eau thermodynamique",
        "Remplacement chaudière par PAC",
        "Entretien et maintenance PAC"
      ],
      prices: [
        { type: "PAC air-eau", price: "8 000-15 000 €" },
        { type: "PAC air-air", price: "3 000-8 000 €" },
        { type: "PAC géothermique", price: "15 000-25 000 €" },
        { type: "Chauffe-eau thermodynamique", price: "2 500-4 500 €" }
      ]
    }
  },
  climatisation: {
    title: "Devis Climatisation",
    description: "Recevez jusqu'à 5 devis gratuits pour l'installation de climatisation",
    seoTitle: "Devis Climatisation Belgique | Installation & Entretien | DevisMaison.be",
    image: "https://images.unsplash.com/photo-1762341123870-d706f257a12e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    content: {
      intro: "Profitez d'un confort optimal toute l'année avec une climatisation adaptée. Nos installateurs certifiés vous conseillent la meilleure solution pour votre habitat.",
      types: [
        "Climatisation split murale",
        "Climatisation multi-split",
        "Climatisation gainable",
        "Climatisation réversible",
        "Climatisation de bureau",
        "Entretien et dépannage"
      ],
      prices: [
        { type: "Split mural (1 unité)", price: "1 500-3 000 €" },
        { type: "Multi-split (2-4 unités)", price: "3 500-8 000 €" },
        { type: "Gainable", price: "5 000-12 000 €" },
        { type: "Entretien annuel", price: "100-200 €" }
      ]
    }
  },
  plomberie: {
    title: "Devis Plomberie",
    description: "Recevez jusqu'à 5 devis gratuits pour vos travaux de plomberie",
    seoTitle: "Devis Plomberie Belgique | Installation & Réparation | DevisMaison.be",
    image: "https://images.pexels.com/photos/6419128/pexels-photo-6419128.jpeg?auto=compress&cs=tinysrgb&w=1200",
    content: {
      intro: "Des travaux de plomberie réalisés par des professionnels qualifiés. De la simple réparation à l'installation complète de votre réseau sanitaire.",
      types: [
        "Installation sanitaire complète",
        "Réparation de fuites",
        "Remplacement tuyauterie",
        "Installation chauffe-eau",
        "Débouchage canalisations",
        "Raccordement appareils"
      ],
      prices: [
        { type: "Réparation fuite", price: "80-250 €" },
        { type: "Installation chauffe-eau", price: "500-2 000 €" },
        { type: "Rénovation plomberie complète", price: "3 000-8 000 €" },
        { type: "Débouchage", price: "100-300 €" }
      ]
    }
  },
  facade: {
    title: "Devis Façade",
    description: "Recevez jusqu'à 5 devis gratuits pour la rénovation de votre façade",
    seoTitle: "Devis Façade Belgique | Ravalement & Isolation | DevisMaison.be",
    image: "https://images.unsplash.com/photo-1632711057336-ddd25c1d921f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    content: {
      intro: "La façade est la première impression de votre maison. Un ravalement de façade protège votre habitation et lui redonne tout son éclat.",
      types: [
        "Ravalement de façade",
        "Isolation par l'extérieur (ITE)",
        "Crépi et enduit",
        "Nettoyage haute pression",
        "Rejointoyage briques",
        "Peinture de façade"
      ],
      prices: [
        { type: "Ravalement simple", price: "30-60 €/m²" },
        { type: "Isolation extérieure (ITE)", price: "100-180 €/m²" },
        { type: "Crépi décoratif", price: "40-80 €/m²" },
        { type: "Rejointoyage", price: "25-50 €/m²" }
      ]
    }
  },
  parquet: {
    title: "Devis Parquet",
    description: "Recevez jusqu'à 5 devis gratuits pour vos travaux de parquet",
    seoTitle: "Devis Parquet Belgique | Pose & Rénovation | DevisMaison.be",
    image: "https://images.pexels.com/photos/15066939/pexels-photo-15066939.jpeg?auto=compress&cs=tinysrgb&w=1200",
    content: {
      intro: "Le parquet apporte chaleur et élégance à votre intérieur. Nos parqueteurs experts vous accompagnent de la sélection à la pose ou la rénovation.",
      types: [
        "Pose parquet massif",
        "Pose parquet contrecollé",
        "Pose parquet stratifié",
        "Ponçage et vitrification",
        "Réparation parquet ancien",
        "Parquet chauffant compatible"
      ],
      prices: [
        { type: "Parquet massif (fourni posé)", price: "60-120 €/m²" },
        { type: "Parquet contrecollé", price: "40-80 €/m²" },
        { type: "Stratifié", price: "20-45 €/m²" },
        { type: "Ponçage + vitrification", price: "25-40 €/m²" }
      ]
    }
  },
  peinture: {
    title: "Devis Peinture",
    description: "Recevez jusqu'à 5 devis gratuits pour vos travaux de peinture",
    seoTitle: "Devis Peinture Belgique | Intérieur & Extérieur | DevisMaison.be",
    image: "https://images.pexels.com/photos/5493654/pexels-photo-5493654.jpeg?auto=compress&cs=tinysrgb&w=1200",
    content: {
      intro: "Une peinture de qualité professionnelle transforme vos espaces. Nos peintres qualifiés assurent une finition impeccable pour vos murs, plafonds et boiseries.",
      types: [
        "Peinture intérieure murs",
        "Peinture plafonds",
        "Peinture extérieure",
        "Peinture décorative",
        "Laquage boiseries",
        "Préparation et enduits"
      ],
      prices: [
        { type: "Peinture murs (par pièce)", price: "200-600 €" },
        { type: "Peinture plafond", price: "15-30 €/m²" },
        { type: "Peinture extérieure", price: "20-40 €/m²" },
        { type: "Laquage portes", price: "100-200 €/porte" }
      ]
    }
  }
};

export const FAMILY_IMAGES_SOURCE = {
  "gros-oeuvre": "entreprise-generale", toiture: "toiture", isolation: "isolation", chassis: "chassis",
  facade: "facade", "renovation-interieure": "renovation", sols: "carrelage", cuisine: "cuisine",
  "salle-de-bain": "salle-de-bain", chauffage: "chauffage", climatisation: "climatisation",
  electricite: "electricite", exterieur: "amenagement-exterieur", extension: "extension-maison",
  menuiserie: "menuiserie", securite: "electricite", humidite: "facade", peinture: "peinture",
  nettoyage: "facade", general: "entreprise-generale",
};

export default function CategoryPage({ category }) {
  const { lang, T } = useLang();
  const C = T.cat;
  const entry = catByKey(category);
  const hasCustom = !!categoryData[category];
  const familyImage = categoryData[FAMILY_IMAGES_SOURCE[entry?.family] || "renovation"]?.image || categoryData.renovation.image;

  const buildGeneric = (lg) => {
    const L = entry[lg];
    const isNl = lg === "nl";
    const name = L.name;
    const lname = name.charAt(0).toLowerCase() + name.slice(1);
    return {
      title: isNl ? `Offerte ${name}` : `Devis ${name}`,
      description: isNl
        ? `Ontvang tot 5 gratis offertes voor ${lname}`
        : `Recevez jusqu'à 5 devis gratuits pour votre projet ${lname}`,
      seoTitle: isNl
        ? `Offerte ${name} België & Luxemburg | Gratis Prijsvergelijking | DevisMaison.be`
        : `Devis ${name} Belgique & Luxembourg | Comparez Gratuitement | DevisMaison.be`,
      image: familyImage,
      content: {
        intro: isNl
          ? `${L.desc}. Vergelijk gratis en vrijblijvend tot 5 offertes van erkende vakmensen in België en Luxemburg en kies de beste prijs-kwaliteit voor uw project.`
          : `${L.desc}. Comparez gratuitement et sans engagement jusqu'à 5 devis d'artisans agréés en Belgique et au Luxembourg et choisissez le meilleur rapport qualité-prix pour votre projet.`,
        types: (FAMILY_TYPES[entry.family] || FAMILY_TYPES.general)[lg],
        prices: CATEGORY_PRICES[category]?.[lg],
        faq: isNl ? [
          { q: `Hoeveel kost ${lname}?`, a: `De prijs hangt af van de omvang, de materialen en de complexiteit van uw project. Vraag gratis tot 5 offertes aan om de prijzen van vakmensen in uw regio te vergelijken.` },
          { q: "Zijn de offertes echt gratis?", a: "Ja, uw aanvraag is 100% gratis en vrijblijvend. U vergelijkt de offertes en kiest zelf of u met een vakman in zee gaat." },
          { q: "Hoe snel word ik gecontacteerd?", a: "Erkende vakmensen uit uw regio nemen doorgaans binnen 24 tot 48 uur contact met u op na uw aanvraag." },
          { q: "Werken jullie in heel België en Luxemburg?", a: "Ja, ons netwerk van vakmensen dekt heel België (Wallonië, Brussel, Vlaanderen) en het Groothertogdom Luxemburg." },
        ] : [
          { q: `Combien coûte un projet de ${lname} ?`, a: `Le prix dépend de l'ampleur, des matériaux et de la complexité de votre projet. Demandez gratuitement jusqu'à 5 devis pour comparer les prix des professionnels de votre région.` },
          { q: "Les devis sont-ils vraiment gratuits ?", a: "Oui, votre demande est 100% gratuite et sans engagement. Vous comparez les devis et choisissez librement de travailler ou non avec un professionnel." },
          { q: "Sous quel délai suis-je contacté ?", a: "Des professionnels agréés de votre région vous contactent généralement sous 24 à 48 heures après votre demande." },
          { q: "Couvrez-vous toute la Belgique et le Luxembourg ?", a: "Oui, notre réseau d'artisans couvre toute la Belgique (Wallonie, Bruxelles, Flandre) et le Grand-Duché de Luxembourg." },
        ],
      },
    };
  };

  const frData = hasCustom ? categoryData[category] : buildGeneric("fr");
  const data = lang === "nl"
    ? (hasCustom ? { ...(categoryDataNL[category] || categoryDataNL.renovation), image: frData.image } : buildGeneric("nl"))
    : frData;
  const prices = data.content.prices;
  const faq = data.content.faq;
  const siblings = ALL_CATS.filter((c) => c.family === entry?.family && c.key !== category);
  const familyLabel = familyByKey(entry?.family)?.[lang === "nl" ? "nl" : "fr"] || "";
  const frPath = catPath(category, "fr");
  const nlPath = catPath(category, "nl");
  const pageUrl = `https://devismaison.be${lang === "nl" ? nlPath : frPath}`;

  return (
    <div className="min-h-screen bg-white" data-testid={`category-page-${category}`}>
      <Helmet>
        <title>{data.seoTitle}</title>
        <meta name="description" content={`${data.description}. ${C.descSuffix}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": data.seoTitle,
          "description": data.description,
          "provider": { "@type": "LocalBusiness", "name": "DevisMaison.be", "url": "https://devismaison.be" },
          "areaServed": ["Belgique", "Luxembourg"],
          "url": pageUrl
        })}</script>
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="DevisMaison.be" />
        <meta property="og:title" content={data.seoTitle} />
        <meta property="og:description" content={data.description} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={data.image} />
        <meta property="og:locale" content={lang === "nl" ? "nl_BE" : "fr_BE"} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={data.seoTitle} />
        <meta name="twitter:description" content={data.description} />
        <meta name="twitter:image" content={data.image} />
      </Helmet>
      <Hreflang frPath={frPath} nlPath={nlPath} />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-24 hero-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Column - Text */}
            <div className="text-center lg:text-left">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-500 mb-4">
                {C.kicker}
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight mb-6">
                {data.title}
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                {data.description}. {C.descSuffix}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a href="#lead-form">
                  <Button
                    className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-6 rounded-full text-lg group"
                    data-testid="category-cta-button"
                  >
                    {C.cta}
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </a>
              </div>
              <div className="flex items-center gap-6 mt-8 justify-center lg:justify-start text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <span>{C.free}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <span>{C.noCommit}</span>
                </div>
              </div>
            </div>

            {/* Right Column - Form */}
            <div
              id="lead-form"
              className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-6 lg:p-8 border border-slate-100"
            >
              <MultiStepForm initialCategory={category} />
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left - Info */}
            <div>
              <h2 className="font-heading text-3xl font-semibold text-slate-900 mb-6">
                {data.title} {C.inBelgium}
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                {data.content.intro}
              </p>

              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                {C.typesTitle}
              </h3>
              <ul className="space-y-3 mb-8">
                {data.content.types.map((type, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-600">
                    <Check className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    {type}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right - Prices and/or FAQ */}
            <div>
              {prices && (
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200">
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-6">
                    {C.pricesTitle}
                  </h3>
                  <div className="space-y-4">
                    {prices.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0"
                      >
                        <span className="text-slate-600">{item.type}</span>
                        <span className="font-semibold text-slate-900">{item.price}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-slate-500 mt-6">
                    {C.pricesNote}
                  </p>
                </div>
              )}
              {faq && (
                <div className={`bg-white rounded-2xl p-8 shadow-sm border border-slate-200 ${prices ? "mt-8" : ""}`}>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-6" data-testid="category-faq-title">
                    {lang === "nl" ? "Veelgestelde vragen" : "Questions fréquentes"}
                  </h3>
                  <div className="space-y-5">
                    {faq.map((item, index) => (
                      <div key={index} className="border-b border-slate-100 last:border-0 pb-4 last:pb-0">
                        <p className="font-semibold text-slate-900 mb-2">{item.q}</p>
                        <p className="text-sm text-slate-600 leading-relaxed">{item.a}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-8 p-6 bg-orange-50 rounded-xl border border-orange-100">
                <h4 className="font-heading font-semibold text-slate-900 mb-2">
                  {C.primesTitle}
                </h4>
                <p className="text-sm text-slate-600 mb-4">
                  {C.primesText}
                </p>
                <a 
                  href="https://www.primesenergie.be/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-orange-500 font-medium text-sm flex items-center gap-1 hover:gap-2 transition-all"
                >
                  {C.primesLink} <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client testimonials */}
      <TestimonialsCarousel family={entry?.family} lang={lang} />

      {/* Internal linking: sibling categories of the same family */}
      {siblings.length > 0 && (
        <section className="py-12 bg-white border-t border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-6">
              {lang === "nl" ? `Andere werken — ${familyLabel}` : `Autres travaux — ${familyLabel}`}
            </h2>
            <div className="flex flex-wrap gap-2" data-testid="sibling-links">
              {siblings.map((c) => (
                <a
                  key={c.key}
                  href={catPath(c.key, lang)}
                  className="px-4 py-2 text-sm bg-slate-100 hover:bg-orange-100 hover:text-orange-700 rounded-full text-slate-700 transition-colors"
                  data-testid={`sibling-${c.key}`}
                >
                  {lang === "nl" ? `Offerte ${c.nl.nav}` : `Devis ${c.fr.nav}`}
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Image Section */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-2xl overflow-hidden">
            <img
              src={data.image}
              alt={data.title}
              loading="lazy"
              className="w-full h-[400px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent flex items-end">
              <div className="p-8 lg:p-12">
                <h3 className="font-heading text-2xl lg:text-3xl font-bold text-white mb-4">
                  {C.bannerTitle}
                </h3>
                <p className="text-slate-200 max-w-2xl mb-6">
                  {C.bannerText}
                </p>
                <a href="#lead-form">
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-full">
                    {C.bannerBtn}
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
